import { useSyncExternalStore } from "react";

/**
 * "Add to home screen" support for the dashboard PWA.
 *
 * - Android (Chrome, Edge, Samsung Internet) fires `beforeinstallprompt`; we hold on to it so our
 *   own button can open the native install sheet.
 * - iOS has no install API, and the Web Share sheet lacks "Add to Home Screen" (only Safari's own
 *   Share button has it), so there we can only show the steps.
 */

export type InstallPlatform = "ios" | "android";

/** Chromium's install prompt event; not in the DOM typings. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "nudgebox:install-dismissed";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
let dialogOpen = false;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Starts listening for the browser's install events. Call once before rendering: Chrome can fire
 * `beforeinstallprompt` before React mounts, and the event can't be recovered after that.
 */
export function captureInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Keep the mini-infobar away; we show our own dialog.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    dialogOpen = false;
    notify();
  });
}

export function detectInstallPlatform(): InstallPlatform | null {
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac; touch support gives it away.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(ua)) return "android";
  return null;
}

/** True when running as the installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Safari's pre-standard flag.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export interface InstallState {
  platform: InstallPlatform | null;
  /** Not installed yet, on a phone or tablet we know how to install on. */
  available: boolean;
  /** The browser can open its own install sheet (Android). */
  canPrompt: boolean;
}

let cachedState: InstallState | null = null;

function getState(): InstallState {
  const platform = detectInstallPlatform();
  const available = platform !== null && !installed && !isStandalone();
  const canPrompt = available && deferredPrompt !== null;
  if (
    !cachedState ||
    cachedState.platform !== platform ||
    cachedState.available !== available ||
    cachedState.canPrompt !== canPrompt
  ) {
    cachedState = { platform, available, canPrompt };
  }
  return cachedState;
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(subscribe, getState, getState);
}

/** Opens the browser's own install sheet. Resolves to whether the user accepted. */
export async function promptInstall(): Promise<boolean> {
  const prompt = deferredPrompt;
  if (!prompt) return false;
  try {
    // Must run synchronously inside the click that asked for it.
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    return outcome === "accepted";
  } finally {
    // A prompt event can only be used once; Chrome fires a fresh one if it's still installable.
    deferredPrompt = null;
    notify();
  }
}

export function useInstallDialogOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => dialogOpen,
    () => false,
  );
}

export function setInstallDialogOpen(open: boolean) {
  if (dialogOpen === open) return;
  dialogOpen = open;
  notify();
}

export function wasInstallDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) !== null;
  } catch {
    // Storage can be blocked (private mode, disabled site data); don't nag on every visit then.
    return true;
  }
}

export function dismissInstall() {
  try {
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
  } catch {
    // Nothing to remember it in.
  }
}
